/**
 * Capture what the user is currently looking at as a {@link ViewContext}
 * (app-shell ADR-014). Read from REAL app state: the live route + the
 * snapshot the rendering view published. No fabrication — fields nothing
 * identifies are omitted.
 *
 * Route params come from `useMatches()`, not `useParams()`. This hook is called
 * from the Shogun panel, which MainLayout mounts at `path: '/'`, and
 * `useParams()` resolves against the CALLER's match — so it returned `{}` for
 * every route and `selected` was dead on every surface. `useMatches()` sees the
 * whole matched chain.
 *
 * The records themselves cannot be re-derived here: the boards' lane groupings
 * and the org chart's expanded set are component-local state. The rendering view
 * publishes them through `shogunViewSnapshotAtom`, and a snapshot for a
 * different pathname is discarded rather than sent stale.
 */

import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useLocation, useMatches } from 'react-router-dom';
import type { ViewContext, ViewRecord } from '@localagent/sdk';
import { shogunViewSnapshotAtom, type ShogunViewSnapshot } from '../../stores/shogun/atoms';

interface SurfaceSpec {
  surface: string;
  primaryType?: string;
  idParam?: string;
}

const SURFACE_BY_SEGMENT: Record<string, SurfaceSpec> = {
  chat: { surface: 'chat', primaryType: 'session', idParam: 'sessionId' },
  agents: { surface: 'agents', primaryType: 'persona', idParam: 'personaId' },
  'agent-processes': {
    surface: 'agent-processes',
    primaryType: 'agent_process',
    idParam: 'processId',
  },
  work: { surface: 'work', primaryType: 'work_item', idParam: 'ticketId' },
  workspaces: { surface: 'workspaces', primaryType: 'workspace', idParam: 'workspaceId' },
  search: { surface: 'search' },
  orgchart: { surface: 'orgchart', primaryType: 'position' },
  docs: { surface: 'docs' },
  integrations: { surface: 'integrations', primaryType: 'integration', idParam: 'connectionId' },
  settings: { surface: 'settings' },
  notifications: { surface: 'notifications' },
};

export function deriveViewContext(
  pathname: string,
  params: Readonly<Record<string, string | undefined>>,
): ViewContext {
  const segment = pathname.split('/').filter(Boolean)[0] ?? 'chat';
  const spec = SURFACE_BY_SEGMENT[segment] ?? { surface: segment };

  const vc: ViewContext = {
    route: pathname,
    surface: spec.surface,
  };
  if (spec.primaryType) {
    vc.primary_type = spec.primaryType;
  }

  const selectedId = spec.idParam ? params[spec.idParam] : undefined;
  if (selectedId && spec.primaryType) {
    const selected: ViewRecord = { type: spec.primaryType, id: selectedId };
    vc.selected = selected;
  }

  return vc;
}

export function mergeViewSnapshot(
  base: ViewContext,
  snapshot: ShogunViewSnapshot | null,
): ViewContext {
  if (!snapshot || snapshot.route !== base.route) return base;
  // The published `selected` carries a label the route param cannot; the route
  // param stands only where the view published none.
  const { selected, ...rest } = snapshot.context;
  return { ...base, ...rest, ...(selected ? { selected } : {}) };
}

/** Merge params across the whole matched chain — see the note above `useParams`. */
export function mergeMatchParams(
  matches: ReadonlyArray<{ params: Readonly<Record<string, string | undefined>> }>,
): Record<string, string | undefined> {
  const merged: Record<string, string | undefined> = {};
  for (const match of matches) {
    for (const [key, value] of Object.entries(match.params)) {
      if (value !== undefined) merged[key] = value;
    }
  }
  return merged;
}

export function useShogunViewContext(): ViewContext {
  const location = useLocation();
  const matches = useMatches();
  const snapshot = useAtomValue(shogunViewSnapshotAtom);

  const params = useMemo(() => mergeMatchParams(matches), [matches]);

  return useMemo(
    () => mergeViewSnapshot(deriveViewContext(location.pathname, params), snapshot),
    [location.pathname, params, snapshot],
  );
}
